/**
 * PasswordInputModal - Reusable password input modal
 * Used for operations requiring password authentication (device deletion, etc.)
 */

import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import './PasswordInputModal.css';

const PasswordInputModal = ({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Enter Password',
  message = 'Please enter your password to continue',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  externalError = null,  // Error message from parent (e.g., "Incorrect password")
  isSubmitting = false,  // When true, disables submit button to prevent multiple clicks
}) => {
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Clear external error when user starts typing
  const displayError = externalError || error;

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Cleanup password on unmount for security
  useEffect(() => {
    return () => {
      setPassword('');
      setShowPassword(false);
      setError('');
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Prevent submission when already submitting (Enter key bypass protection)
    if (isSubmitting) return;

    if (!password.trim()) {
      setError(t('PasswordInputModal.passwordRequired'));
      return;
    }

    setError('');
    onConfirm(password);
    setPassword('');  // Clear for security
  };

  const handleCancel = () => {
    setPassword('');
    setError('');
    onCancel();
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) setError('');  // Clear error on input
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div
        className="modal-container modal-password"
        data-testid="password-input-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button 
            type="button" 
            className="modal-close-btn"
            onClick={handleCancel}
            aria-label="Close"
          >
            <ion-icon name="close-outline" />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <p className="modal-message">{message}</p>
          
          <form onSubmit={handleSubmit}>
            <div className="password-input-group">
              <label htmlFor="password-input" className="password-label">
                {t('PasswordInputModal.passwordLabel')}
              </label>
              <div className="password-input-container">
                <input
                  ref={inputRef}
                  id="password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  className={`password-input ${displayError ? 'error' : ''}`}
                  placeholder={t('PasswordInputModal.enterPassword')}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <ion-icon name={showPassword ? 'eye-off-outline' : 'eye-outline'} />
                </button>
              </div>
              {displayError && <span className="password-error">{displayError}</span>}
            </div>

            {/* Footer buttons */}
            <div className="modal-footer">
              <button
                type="button"
                className="password-modal-btn password-modal-btn-cancel"
                onClick={handleCancel}
              >
                {cancelText}
              </button>
              <button
                type="submit"
                className="password-modal-btn password-modal-btn-confirm"
                disabled={isSubmitting}
              >
                {confirmText}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

PasswordInputModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  externalError: PropTypes.string,
  isSubmitting: PropTypes.bool,
};

export default PasswordInputModal;
